import { makeAutoObservable, runInAction } from "mobx";
import {
  createSwayyProductImage,
  deleteSwayyProductImage,
  getSwayyProductDetail,
  isCancelledSwayyRequest,
  listSwayyProductVariants,
  mapSwayyProductImages,
  normalizeSwayyApiError,
  reorderSwayyProductImages,
  SwayyApiError,
  updateSwayyProductImage,
  uploadSwayyProductImages,
} from "../api/SwayyProductsAPI";
import { SwayyCatalogLoadState } from "../models/common/SwayyCatalogLoadState";
import {
  SwayyProductImage,
  SwayyProductImageType,
} from "../models/images/SwayyProductImage";
import { SwayyProductVariant } from "../models/variants/SwayyProductVariant";
import {
  productImageTypeSchema,
  validateImageUploadFiles,
  validateUpdateImageMetaForm,
  zodFirstMessage,
} from "../validation/swayyFormSchemas";

export type SwayyImagesScope = "product" | "variant";

export class SwayyProductImagesStore {
  images: SwayyProductImage[] = [];
  variants: SwayyProductVariant[] = [];
  scope: SwayyImagesScope = "product";
  selectedVariantId: number | null = null;
  uploadImageType: SwayyProductImageType = "square";
  loadState: SwayyCatalogLoadState = "idle";
  errorMessage = "";
  isUploading = false;
  isUpdating = false;
  imagesTabLoadedProductId: number | null = null;
  private abortController?: AbortController;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get scopedImages(): SwayyProductImage[] {
    if (this.scope === "product") {
      return this.images.filter((image) => image.variantId == null);
    }
    if (this.selectedVariantId == null) return [];
    return this.images.filter(
      (image) => image.variantId === this.selectedVariantId,
    );
  }

  get selectedVariant(): SwayyProductVariant | null {
    if (this.selectedVariantId == null) return null;
    return (
      this.variants.find((variant) => variant.id === this.selectedVariantId) ??
      null
    );
  }

  get canUpload(): boolean {
    if (this.scope === "product") return true;
    return this.selectedVariantId != null;
  }

  imageCountFor(variantId: number): number {
    return this.images.filter((image) => image.variantId === variantId).length;
  }

  setScope(scope: SwayyImagesScope): void {
    this.scope = scope;
    if (
      scope === "variant" &&
      this.selectedVariantId == null &&
      this.variants.length > 0
    ) {
      this.selectedVariantId = this.variants[0].id;
    }
  }

  setSelectedVariantId(variantId: number | null): void {
    this.selectedVariantId = variantId;
  }

  setUploadImageType(imageType: SwayyProductImageType): void {
    const parsed = productImageTypeSchema.safeParse(imageType);
    if (!parsed.success) return;
    this.uploadImageType = parsed.data;
  }

  async loadImagesTab(productId: number): Promise<SwayyApiError | null> {
    this.abortController?.abort();
    const controller = new AbortController();
    this.abortController = controller;
    this.loadState = "loading";
    this.errorMessage = "";

    try {
      const [detail, variants] = await Promise.all([
        getSwayyProductDetail(productId, controller.signal),
        listSwayyProductVariants(productId, controller.signal),
      ]);
      runInAction(() => {
        this.images = mapSwayyProductImages(detail.images);
        this.variants = variants;
        if (
          this.scope === "variant" &&
          this.selectedVariantId == null &&
          variants.length > 0
        ) {
          this.selectedVariantId = variants[0].id;
        }
        if (
          this.selectedVariantId != null &&
          !variants.some((variant) => variant.id === this.selectedVariantId)
        ) {
          this.selectedVariantId = variants[0]?.id ?? null;
        }
        this.loadState = "ready";
        this.imagesTabLoadedProductId = productId;
      });
      return null;
    } catch (error) {
      if (isCancelledSwayyRequest(error)) return null;
      const apiError = normalizeSwayyApiError(
        error,
        "Unable to load product images.",
      );
      runInAction(() => {
        this.loadState = "error";
        this.errorMessage = apiError.message;
        this.imagesTabLoadedProductId = null;
      });
      return apiError;
    }
  }

  async uploadAndCreate(
    productId: number,
    files: File[],
  ): Promise<SwayyApiError | null> {
    if (!this.canUpload) {
      return new SwayyApiError("Select a variant before uploading images.");
    }
    const filesCheck = validateImageUploadFiles(files);
    if (!filesCheck.ok) {
      return new SwayyApiError(filesCheck.message, 400);
    }
    const imageTypeCheck = productImageTypeSchema.safeParse(
      this.uploadImageType,
    );
    if (!imageTypeCheck.success) {
      return new SwayyApiError(zodFirstMessage(imageTypeCheck.error), 400);
    }

    this.isUploading = true;
    try {
      const urls = await uploadSwayyProductImages(productId, files);
      const variantId =
        this.scope === "variant" ? this.selectedVariantId : null;
      const created: SwayyProductImage[] = [];
      for (let index = 0; index < urls.length; index += 1) {
        const image = await createSwayyProductImage(productId, {
          url: urls[index],
          image_type: imageTypeCheck.data,
          alt: null,
          is_default: this.scopedImages.length === 0 && index === 0,
          variant_id: variantId,
        });
        created.push(image);
      }
      runInAction(() => {
        const createdIds = new Set(created.map((image) => image.id));
        const kept = this.images.filter((image) => !createdIds.has(image.id));
        this.images = [...kept, ...created].sort(
          (a, b) => a.priority - b.priority || a.id - b.id,
        );
      });
      return null;
    } catch (error) {
      return normalizeSwayyApiError(error, "Unable to upload images.");
    } finally {
      runInAction(() => {
        this.isUploading = false;
      });
    }
  }

  async setDefault(imageId: number): Promise<SwayyApiError | null> {
    this.isUpdating = true;
    try {
      const updated = await updateSwayyProductImage(imageId, {
        is_default: true,
      });
      runInAction(() => {
        this.images = this.images.map((image) => {
          const sameScope =
            image.variantId === updated.variantId ||
            (image.variantId == null && updated.variantId == null);
          if (image.id === updated.id) return updated;
          if (sameScope && image.isDefault) {
            return { ...image, isDefault: false };
          }
          return image;
        });
      });
      return null;
    } catch (error) {
      return normalizeSwayyApiError(error, "Unable to set default image.");
    } finally {
      runInAction(() => {
        this.isUpdating = false;
      });
    }
  }

  async updateImageMeta(
    imageId: number,
    payload: { alt: string; imageType: SwayyProductImageType },
  ): Promise<SwayyApiError | null> {
    const metaCheck = validateUpdateImageMetaForm(payload);
    if (!metaCheck.ok) {
      return new SwayyApiError(metaCheck.message, 400);
    }

    this.isUpdating = true;
    try {
      const updated = await updateSwayyProductImage(imageId, {
        alt: payload.alt.trim() || null,
        image_type: payload.imageType,
      });
      runInAction(() => {
        this.images = this.images.map((image) =>
          image.id === updated.id ? updated : image,
        );
      });
      return null;
    } catch (error) {
      return normalizeSwayyApiError(error, "Unable to update image.");
    } finally {
      runInAction(() => {
        this.isUpdating = false;
      });
    }
  }

  async deleteImage(
    productId: number,
    imageId: number,
  ): Promise<SwayyApiError | null> {
    this.isUpdating = true;
    try {
      await deleteSwayyProductImage(imageId);
      const error = await this.loadImagesTab(productId);
      return error;
    } catch (error) {
      return normalizeSwayyApiError(error, "Unable to delete image.");
    } finally {
      runInAction(() => {
        this.isUpdating = false;
      });
    }
  }

  async reorderImages(
    productId: number,
    orderedImageIds: number[],
  ): Promise<SwayyApiError | null> {
    const currentIds = this.scopedImages.map((image) => image.id);
    if (
      orderedImageIds.length < 2 ||
      orderedImageIds.length !== currentIds.length ||
      orderedImageIds.every((id, index) => id === currentIds[index])
    ) {
      return null;
    }

    this.isUpdating = true;
    try {
      const updatedScope = await reorderSwayyProductImages(productId, {
        variant_id: this.scope === "variant" ? this.selectedVariantId : null,
        ordered_image_ids: orderedImageIds,
      });
      runInAction(() => {
        const otherScope = this.images.filter((image) => {
          if (this.scope === "product") return image.variantId != null;
          return image.variantId !== this.selectedVariantId;
        });
        this.images = [...otherScope, ...updatedScope].sort(
          (a, b) => a.priority - b.priority || a.id - b.id,
        );
      });
      return null;
    } catch (error) {
      return normalizeSwayyApiError(error, "Unable to reorder images.");
    } finally {
      runInAction(() => {
        this.isUpdating = false;
      });
    }
  }

  async moveImage(
    productId: number,
    imageId: number,
    direction: "up" | "down",
  ): Promise<SwayyApiError | null> {
    const scoped = [...this.scopedImages];
    const index = scoped.findIndex((image) => image.id === imageId);
    if (index < 0) return null;
    const swapWith = direction === "up" ? index - 1 : index + 1;
    if (swapWith < 0 || swapWith >= scoped.length) return null;

    const reordered = [...scoped];
    const [moved] = reordered.splice(index, 1);
    reordered.splice(swapWith, 0, moved);
    return this.reorderImages(
      productId,
      reordered.map((image) => image.id),
    );
  }

  dispose(): void {
    this.abortController?.abort();
    this.abortController = undefined;
  }
}
